import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Camera } from 'lucide-react';
import { useAppContext } from '@/contexts/AppContext';
import { toast } from '@/components/ui/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

const UserProfile = () => {
  const { currentUser, updateUserProfile } = useAppContext();
  const [editing, setEditing] = useState(false);
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    firstName: currentUser?.name?.split(' ')[0] || '',
    email: currentUser?.email || '',
    password: '',
    confirmPassword: '',
    profilePicture: currentUser?.profilePicture || ''
  });

  const handleSave = async () => {
    if (formData.password && formData.password !== formData.confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }

    const success = await updateUserProfile({
      firstName: formData.firstName,
      email: formData.email,
      password: formData.password || undefined,
      profilePicture: formData.profilePicture
    });

    if (success) {
      setEditing(false);
      setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({ ...prev, profilePicture: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className={`${isMobile ? 'p-0' : 'flex justify-center'}`}>
      <Card className={`${isMobile ? 'w-full border-0 shadow-none' : 'max-w-md'}`}>
        <CardHeader className={isMobile ? 'pb-4' : ''}>
          <CardTitle className={isMobile ? 'text-xl' : 'text-2xl'}>
            User Profile
          </CardTitle>
        </CardHeader>
        <CardContent className={`space-y-4 ${isMobile ? 'px-4' : ''}`}>
          <ScrollArea className={isMobile ? 'max-h-[70vh]' : 'max-h-[80vh]'}>
            <div className="space-y-4 pr-2">
              <div className="flex flex-col items-center space-y-2">
                <div className="relative">
                  <Avatar className={`${isMobile ? 'w-20 h-20' : 'w-24 h-24'}`}>
                    <AvatarImage src={formData.profilePicture} />
                    <AvatarFallback className={isMobile ? 'text-lg' : 'text-xl'}>
                      {formData.firstName?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {editing && (
                    <label className="absolute bottom-0 right-0 bg-primary text-white p-1 rounded-full cursor-pointer hover:bg-primary/90 transition-colors">
                      <Camera className="w-4 h-4" />
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    </label>
                  )}
                </div>
                {editing && (
                  <p className={`text-center text-muted-foreground ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    Tap camera icon to change photo
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="firstName" className={isMobile ? 'text-sm' : ''}>
                    First Name
                  </Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    disabled={!editing}
                    className={isMobile ? 'text-base h-12' : ''}
                  />
                </div>

                <div>
                  <Label htmlFor="email" className={isMobile ? 'text-sm' : ''}>
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!editing}
                    className={isMobile ? 'text-base h-12' : ''}
                  />
                </div>

                {editing && (
                  <>
                    <div>
                      <Label htmlFor="password" className={isMobile ? 'text-sm' : ''}>
                        New Password
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Leave blank to keep current"
                        className={isMobile ? 'text-base h-12' : ''}
                      />
                    </div>

                    <div>
                      <Label htmlFor="confirmPassword" className={isMobile ? 'text-sm' : ''}>
                        Confirm Password
                      </Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className={isMobile ? 'text-base h-12' : ''}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </ScrollArea>

          <div className={`flex gap-2 ${isMobile ? 'flex-col pt-4 border-t' : ''}`}>
            {editing ? (
              <>
                <Button 
                  onClick={handleSave} 
                  className={`flex-1 ${isMobile ? 'h-12 text-base' : ''}`}
                >
                  Save Changes
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setEditing(false)} 
                  className={`flex-1 ${isMobile ? 'h-12 text-base' : ''}`}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button 
                onClick={() => setEditing(true)} 
                className={`w-full ${isMobile ? 'h-12 text-base' : ''}`}
              >
                Edit Profile
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserProfile;